import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import { preloadSiteContent } from '@/hooks/useSiteContent'
import EditableText from '@/components/EditableText'

export default function Accueil() {
  useEffect(() => {
    preloadSiteContent()
  }, [])

  const portes = [
    {
      nom: 'Comprendre',
      lien: '/comprendre',
      description: 'Voir ce qui agit, en soi, sous la surface'
    },
    {
      nom: 'Ressentir',
      lien: '/experimenter',
      description: 'Activer le corps, ouvrir les perceptions'
    },
    {
      nom: 'Transformer',
      lien: '/transformation',
      description: 'Laisser l\'énergie circuler, rester présent'
    }
  ]

  return (
    <div className="fade-in">
      {/* Hero — activation, pas berceuse */}
      <section className="min-h-[90vh] flex flex-col items-center justify-center px-6 py-24 text-center">
        <div className="max-w-4xl">
          <EditableText
            pageKey="accueil"
            sectionKey="hero_eyebrow"
            fallback="— Activation consciente —"
            as="p"
            contentType="subtitle"
            className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-12 shimmer"
          />

          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.15] text-[#f5f1ea] mb-10">
            <EditableText pageKey="accueil" sectionKey="hero_title_1" fallback="Laboratoire" as="span" contentType="title" /><br />
            <EditableText pageKey="accueil" sectionKey="hero_title_2" fallback="de transformation." as="span" contentType="title" className="italic text-[#c9a96e]" />
          </h1>

          <div className="w-px h-16 bg-gradient-to-b from-[#c9a96e]/60 to-transparent mx-auto mb-10" />

          <EditableText
            pageKey="accueil"
            sectionKey="hero_quote"
            fallback="Tu n'es pas là pour t'endormir."
            as="p"
            contentType="paragraph"
            className="font-serif text-xl md:text-2xl text-[#f5f1ea]/85 leading-relaxed max-w-2xl mx-auto mb-4 italic"
          />
          <EditableText
            pageKey="accueil"
            sectionKey="hero_quote_2"
            fallback="Tu es là pour t'ouvrir."
            as="p"
            contentType="paragraph"
            className="font-serif text-xl md:text-2xl text-[#c9a96e]/90 leading-relaxed max-w-2xl mx-auto mb-8 italic"
          />
          <EditableText
            pageKey="accueil"
            sectionKey="hero_desc"
            fallback={"Le corps est déjà vivant. La conscience aussi.\nMais une grande partie reste fermée.\nIci, on active ce qui est déjà là."}
            as="p"
            contentType="paragraph"
            multiline
            className="text-base md:text-lg text-[#8a8580] leading-loose max-w-xl mx-auto font-light whitespace-pre-line"
          />
        </div>
      </section>

      {/* Three doors */}
      <section className="px-6 lg:px-12 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <EditableText
              pageKey="accueil"
              sectionKey="baseline"
              fallback="Voir. Ressentir. S'ouvrir."
              as="p"
              contentType="paragraph"
              className="font-serif text-2xl md:text-3xl text-[#c9a96e] italic tracking-wide"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-4">
            {portes.map((porte, i) => (
              <Link
                key={porte.nom}
                to={porte.lien}
                className="group relative block"
                style={{ animationDelay: `${i * 0.3}s` }}
              >
                <div className="relative px-8 py-16 md:py-20 border border-[#c9a96e]/20 hover:border-[#c9a96e]/60 transition-all duration-700 text-center overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#c9a96e]/0 via-[#c9a96e]/5 to-[#c9a96e]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />

                  <div className="relative">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/60 mb-6">
                      0{i + 1}
                    </p>
                    <h3 className="font-serif text-3xl md:text-4xl text-[#f5f1ea] group-hover:text-[#c9a96e] transition-colors duration-700 mb-4">
                      {porte.nom}
                    </h3>
                    <div className="w-8 h-px bg-[#c9a96e]/40 mx-auto mb-6 group-hover:w-16 transition-all duration-700" />
                    <p className="text-sm text-[#8a8580] font-light leading-relaxed max-w-xs mx-auto">
                      {porte.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Invitation — haute fréquence */}
      <section className="px-6 py-32">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="w-12 h-px bg-[#c9a96e] mx-auto mb-12" />

          <EditableText
            pageKey="accueil"
            sectionKey="invitation_quote"
            fallback="Ce n'est pas un retrait. C'est une entrée plus profonde dans le vivant."
            as="p"
            contentType="paragraph"
            className="font-serif text-xl md:text-2xl text-[#f5f1ea]/90 leading-loose italic"
          />

          <EditableText
            pageKey="accueil"
            sectionKey="invitation_text"
            fallback={"Présence lucide.\nCorps ouvert.\nÉnergie qui monte.\nConscience active."}
            as="p"
            contentType="paragraph"
            multiline
            className="text-base text-[#8a8580] leading-loose font-light pt-8 whitespace-pre-line"
          />

          <div className="pt-12">
            <Link
              to="/accompagnements"
              className="inline-block text-sm tracking-[0.3em] uppercase text-[#c9a96e] border-b border-[#c9a96e]/30 hover:border-[#c9a96e] pb-2 transition-all duration-500"
            >
              <EditableText
                pageKey="accueil"
                sectionKey="invitation_cta"
                fallback="Ouverture — activation vibratoire"
                as="span"
                contentType="button"
              />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
